import { useTerminalsStateRE } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { LogoTile } from "@/components/LogoTile";

import { useCallback } from "react";

type Props = {
  customerId: string;
  customerName: string;
  size?: number;
  className?: string;
  editable?: boolean;
};

export const CustomerLogoView: React.FC<Props> = ({
  customerId,
  customerName,
  size = 100,
  className = "absolute top-0 right-0",
  editable = true,
}: Props) => {
  const { currentTerminal } = useTerminalsStateRE();
  const trpcUtils = trpc.useUtils();

  const {
    data: logoData,
    refetch,
    isLoading,
  } = trpc.customerStorage.getLogo.useQuery({
    terminalId: currentTerminal.id,
    customerId,
  });

  const { mutate: deleteLogoFromStorage } =
    trpc.customerStorage.deleteLogo.useMutation();

  const getUploadUrl = useCallback(
    async (filename: string) => {
      return trpcUtils.customerStorage.getUploadUrl.fetch({
        terminalId: currentTerminal.id,
        customerId,
        filename,
      });
    },
    [trpcUtils, currentTerminal.id, customerId],
  );

  const { mutateAsync: cleanUpLogoFilesMutation } =
    trpc.customerStorage.cleanUpLogoFiles.useMutation();

  const cleanUpLogoFiles = useCallback(
    async (oldFileName: string) => {
      await cleanUpLogoFilesMutation({
        terminalId: currentTerminal.id,
        customerId,
        oldFileName,
      });
    },
    [cleanUpLogoFilesMutation, currentTerminal.id, customerId],
  );

  const deleteLogo = useCallback(
    (fileName: string, options: { onSuccess: () => void }) => {
      deleteLogoFromStorage(
        {
          terminalId: currentTerminal.id,
          customerId,
          fileName,
        },
        { onSuccess: options.onSuccess },
      );
    },
    [deleteLogoFromStorage, currentTerminal.id, customerId],
  );

  if (isLoading) {
    return null;
  }

  return (
    <div className={className}>
      <LogoTile
        logoUrl={logoData?.url ?? null}
        displayName={customerName}
        size={size}
        enableFileMenu
        buttonPosition="right"
        uploadConfig={
          editable
            ? {
                getUploadUrl,
                cleanUpLogoFiles,
                deleteLogo,
                onComplete: refetch,
                currentFileName: logoData?.name,
              }
            : undefined
        }
      />
    </div>
  );
};
